import { Repetition, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import React, { useCallback, useEffect, useState } from "react";
import { WORKING_DAYS_PER_WEEK } from "../../utils";
import { useI18n } from "../../hooks/i18n-hooks";
import QuantityField from "../QuantityField";

export type TimePerWeekClassicComponentProps = {
    workingTime: WorkingTimeClassicType;
    onChange: (v: { workingTime: WorkingTimeClassicType }) => void;
    hoursPerWeek: number;
    error?: string;
    disabled?: boolean;
};

const convertUnitCount = (
    unitCount: number | undefined,
    from: Unit,
    to: Unit,
    hoursPerWeek: number,
): number | undefined => {
    if (unitCount == undefined) {
        return undefined;
    }
    if (from === to) {
        return unitCount;
    }
    const hoursPerDay = hoursPerWeek / WORKING_DAYS_PER_WEEK;
    if (from === Unit.Hours && to === Unit.Days) {
        return Math.round(unitCount / hoursPerDay);
    }
    if (from === Unit.Days && to === Unit.Hours) {
        return Math.round(unitCount * hoursPerDay);
    }
    throw new Error("Invalid conversion");
};

const MonthlyTimePerWeekClassicComponent: React.FC<TimePerWeekClassicComponentProps> = ({
    workingTime,
    onChange,
    disabled = false,
    hoursPerWeek,
}) => {
    /* Component is using minutes as value */
    const i18n = useI18n();
    const unit = Unit.Hours;
    const repetition = Repetition.Week;
    const [value, setValue] = useState<number | undefined>(workingTime.unitCount || 0);

    useEffect(() => {
        // Notify parent if the upstream `workingTime` is in the wrong unit /
        // repetition for this component — converts to (Hours, Week). External
        // sync, gated on a discrete mismatch, no cascading loop.
        if (workingTime.unit !== unit || workingTime.repetition !== repetition) {
            const converted = convertUnitCount(
                workingTime.unitCount || undefined,
                workingTime.unit, // Unit.Days
                unit,
                hoursPerWeek,
            );
            onChange({
                workingTime: { repetition, unit, unitCount: converted ?? null, dailyHours: null },
            });
        }
    }, [workingTime, unit, repetition, hoursPerWeek, onChange]);

    const updateWorkingTime = useCallback(
        (newValue?: number) => {
            setValue(newValue);
            const valueAsNumber = Number(newValue?.toString());
            let changedValue: WorkingTimeClassicType | null;
            if (isNaN(valueAsNumber) || valueAsNumber <= 0 || valueAsNumber > 24 * 7) {
                changedValue = { repetition, unit, unitCount: null, dailyHours: null };
            } else {
                changedValue = { repetition, unit, unitCount: valueAsNumber, dailyHours: null };
            }
            onChange({ workingTime: changedValue });
        },
        [onChange, repetition, unit],
    );

    // Resync local input AND renormalize the parent's persisted spec when the
    // upstream duration changes. Kept as an effect (rather than a render-phase
    // "adjust state on prop change" block) so the mount-time normalization —
    // clamping out-of-range `unitCount` and propagating `onChange` upward —
    // still runs on the first render.
    useEffect(() => {
        const newDuration = workingTime.unitCount || undefined;
        if (value !== newDuration) {
            updateWorkingTime(newDuration);
        }
    }, [updateWorkingTime, value, workingTime.unitCount]);

    return (
        <QuantityField
            label={i18n.t("utils.working_time.hours_label")}
            style={{ flexShrink: 1, flexGrow: 0, width: "auto" }}
            selectTextOnFocus
            textAlign="right"
            decimalPlaces={2}
            disabled={disabled}
            value={value}
            maxValue={hoursPerWeek}
            onChange={updateWorkingTime}
            keyboardType="decimal-pad"
        />
    );
};

export default MonthlyTimePerWeekClassicComponent;
